import * as React from 'react';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core';
import {
  LEAD_ROLES,
  MARKETING_ROLES,
  PROFILE_ROLES,
  SHORT_LINK_ROLES,
  SMS_ROLES,
} from 'core/hooks';
import { UserRole } from 'types';

// Small pre-bootstrap pages stay static (they also render outside Suspense).
import { ErrorPage } from 'pages/ErrorPage';
import { Splash } from 'pages/Splash';

import { PATHS, PATHS_PARAMS } from './config';

const RELOAD_FLAG = 'lazy-chunk-reloaded';

/**
 * Route-level code splitting (spec 0008 round 2): every section is its own
 * chunk, so a finance-only user no longer downloads the communicator,
 * chart.js, the DnD board, etc. before first paint.
 *
 * Deploys wipe old chunks from the bucket (`aws s3 rm` / DELETE_FLAG in
 * bitbucket-pipelines.yml), so a client still holding a pre-deploy
 * index.html would crash on its first lazy navigation — one forced reload
 * picks up the fresh manifest; the sessionStorage flag prevents a reload
 * loop when the chunk is genuinely broken.
 *
 * IMPORTANT: import page FOLDERS directly ('pages/Students'), never the
 * `pages` barrel — one barrel import would pull every page back into the
 * main chunk (package.json has no sideEffects field, webpack 4 keeps it).
 */
const lazyPage = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) =>
  lazy(() =>
    factory()
      .then((module) => {
        window.sessionStorage.removeItem(RELOAD_FLAG);
        return module;
      })
      .catch((error) => {
        if (!window.sessionStorage.getItem(RELOAD_FLAG)) {
          window.sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          // Keep the Suspense fallback up while the reload happens.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }),
  );

const Debtors = lazyPage(() =>
  import('pages/Debtors').then((m) => ({ default: m.Debtors })),
);
const McpAccess = lazyPage(() =>
  import('pages/McpAccess').then((m) => ({ default: m.McpAccess })),
);
const UsersSettings = lazyPage(() =>
  import('pages/UsersSettings').then((m) => ({ default: m.UsersSettings })),
);
const Students = lazyPage(() =>
  import('pages/Students').then((m) => ({ default: m.Students })),
);
const PlatformSettings = lazyPage(() =>
  import('pages/PlatformSettings').then((m) => ({
    default: m.PlatformSettings,
  })),
);
const CourseDetail = lazyPage(() =>
  import('pages/CourseDetail').then((m) => ({ default: m.CourseDetail })),
);
const Journal = lazyPage(() =>
  import('pages/Journal').then((m) => ({ default: m.Journal })),
);
const Finances = lazyPage(() =>
  import('pages/Finances').then((m) => ({ default: m.Finances })),
);
const TeachersOption = lazyPage(() =>
  import('pages/TeachersOption').then((m) => ({ default: m.TeachersOption })),
);
const Performance = lazyPage(() =>
  import('pages/Performance').then((m) => ({ default: m.Performance })),
);
const ZoomRecordings = lazyPage(() =>
  import('pages/ZoomRecordings').then((m) => ({ default: m.ZoomRecordings })),
);
const PandaDocAnalytics = lazyPage(() =>
  import('pages/PandaDocAnalytics').then((m) => ({
    default: m.PandaDocAnalytics,
  })),
);
const PandaDocProvisioning = lazyPage(() =>
  import('pages/PandaDocProvisioning').then((m) => ({
    default: m.PandaDocProvisioning,
  })),
);
const FakturowniaInvoices = lazyPage(() =>
  import('pages/FakturowniaInvoices').then((m) => ({
    default: m.FakturowniaInvoices,
  })),
);
const FakturowniaInvoiceDetail = lazyPage(() =>
  import('pages/FakturowniaInvoices').then((m) => ({
    default: m.FakturowniaInvoiceDetail,
  })),
);
const FakturowniaExpenses = lazyPage(() =>
  import('pages/FakturowniaExpenses').then((m) => ({
    default: m.FakturowniaExpenses,
  })),
);
const FinanceDashboard = lazyPage(() =>
  import('pages/FinanceDashboard').then((m) => ({
    default: m.FinanceDashboard,
  })),
);
const CashFlowReport = lazyPage(() =>
  import('pages/CashFlowReport').then((m) => ({ default: m.CashFlowReport })),
);
const PnlExport = lazyPage(() =>
  import('pages/PnlExport').then((m) => ({ default: m.PnlExport })),
);

const PaymentCalendar = lazyPage(() =>
  import('pages/PaymentCalendar').then((m) => ({
    default: m.PaymentCalendar,
  })),
);
const StudentDetail = lazyPage(() =>
  import('pages/StudentDetail').then((m) => ({ default: m.StudentDetail })),
);
const GroupDetail = lazyPage(() =>
  import('pages/GroupDetail').then((m) => ({ default: m.GroupDetail })),
);
const TeacherDetail = lazyPage(() =>
  import('pages/TeacherDetail').then((m) => ({ default: m.TeacherDetail })),
);
const AccountManagerDashboard = lazyPage(() =>
  import('pages/AccountManagerDashboard').then((m) => ({
    default: m.AccountManagerDashboard,
  })),
);
const Communicator = lazyPage(() =>
  import('pages/Communicator').then((m) => ({ default: m.Communicator })),
);
const LessonWhiteboard = lazyPage(() =>
  import('pages/LessonWhiteboard').then((m) => ({
    default: m.LessonWhiteboardPage,
  })),
);
const Surveys = lazyPage(() =>
  import('pages/Surveys').then((m) => ({ default: m.Surveys })),
);
const SurveyBuilder = lazyPage(() =>
  import('pages/SurveyBuilder').then((m) => ({ default: m.SurveyBuilder })),
);
const SurveyResults = lazyPage(() =>
  import('pages/SurveyResults').then((m) => ({ default: m.SurveyResults })),
);
const SurveySummary = lazyPage(() =>
  import('pages/SurveySummary').then((m) => ({ default: m.SurveySummary })),
);
const SurveyTeachers = lazyPage(() =>
  import('pages/SurveyTeachers').then((m) => ({ default: m.SurveyTeachers })),
);
const SurveyAnalytics = lazyPage(() =>
  import('pages/SurveyAnalytics').then((m) => ({ default: m.SurveyAnalytics })),
);
const SurveyTags = lazyPage(() =>
  import('pages/SurveyTags').then((m) => ({ default: m.SurveyTags })),
);
// Spec 0018 career centre. Import the page FOLDER, never the `pages` barrel —
// a barrel import drags every page into the main chunk.
const CareerProfessions = lazyPage(() =>
  import('pages/CareerDictionaries').then((m) => ({ default: m.Professions })),
);
const CareerMedals = lazyPage(() =>
  import('pages/CareerDictionaries').then((m) => ({ default: m.Medals })),
);
const CareerSiteCourses = lazyPage(() =>
  import('pages/CareerDictionaries').then((m) => ({ default: m.SiteCourses })),
);
const SkillsDictionary = lazyPage(() =>
  import('pages/CareerDictionaries').then((m) => ({
    default: m.SkillsDictionary,
  })),
);
const CareerCenter = lazyPage(() =>
  import('pages/CareerCenter').then((m) => ({ default: m.CareerCenter })),
);
const Dofinansowanie = lazyPage(() =>
  import('pages/Dofinansowanie').then((m) => ({ default: m.Dofinansowanie })),
);
const Contracts = lazyPage(() =>
  import('pages/Contracts').then((m) => ({ default: m.Contracts })),
);
const ContractTemplates = lazyPage(() =>
  import('pages/Contracts').then((m) => ({ default: m.ContractTemplates })),
);
const ShortLinks = lazyPage(() =>
  import('pages/ShortLinks').then((m) => ({ default: m.ShortLinks })),
);
const Sms = lazyPage(() =>
  import('pages/Sms').then((m) => ({ default: m.Sms })),
);
const Leads = lazyPage(() =>
  import('pages/Leads').then((m) => ({ default: m.Leads })),
);
const Profile = lazyPage(() =>
  import('pages/Profile').then((m) => ({ default: m.Profile })),
);
// ТЗ 0088. Папка страницы, не барабан `pages` (см. примечание выше).
const Marketing = lazyPage(() =>
  import('pages/Marketing').then((m) => ({ default: m.Marketing })),
);

const ROUTES = {
  // Must precede `pandadoc` and `debtors`: `/debtors/pandadoc/provisioning` is
  // a sub-path of both, and neither of those routes is `exact`.
  pandadocProvisioning: (
    <Route
      path={PATHS.debtorsPandadocProvisioning}
      component={PandaDocProvisioning}
      key={PATHS.debtorsPandadocProvisioning}
    />
  ),
  // Must precede `debtors`: `/debtors/pandadoc` is a sub-path of `/debtors`
  // and the `debtors` route is not `exact`.
  pandadoc: (
    <Route
      path={PATHS.debtorsPandadoc}
      component={PandaDocAnalytics}
      key={PATHS.debtorsPandadoc}
    />
  ),
  debtors: (
    <Route path={PATHS.debtors} component={Debtors} key={PATHS.debtors} />
  ),
  mcpAccess: (
    <Route path={PATHS.mcpAccess} component={McpAccess} key={PATHS.mcpAccess} />
  ),
  debtorsContributions: (
    <Route
      path={`${PATHS.contributions}/${PATHS_PARAMS.contributionNum}`}
      component={Debtors}
      key={`${PATHS.contributions}/${PATHS_PARAMS.contributionNum}`}
    />
  ),
  // Detail must precede list — list has no `exact` on the prefix branch.
  studentDetail: (
    <Route
      path={PATHS.studentDetail}
      component={StudentDetail}
      key={PATHS.studentDetail}
    />
  ),
  students: (
    <Route
      path={`${PATHS.students}`}
      exact
      component={Students}
      key={`${PATHS.students}`}
    />
  ),
  groupDetail: (
    <Route
      path={PATHS.groupDetail}
      component={GroupDetail}
      key={PATHS.groupDetail}
    />
  ),
  // Must precede teachers branch — `/teachers/:id/details` shadows the list.
  teacherDetail: (
    <Route
      path={PATHS.teacherDetail}
      component={TeacherDetail}
      key={PATHS.teacherDetail}
    />
  ),
  users: (
    <Route path={PATHS.users} component={UsersSettings} key={PATHS.users} />
  ),
  // ТЗ 0040. Обязана идти ПЕРЕД `settings`: тот маршрут не `exact`, и
  // `/settings/courses/12` отрисовал бы список настроек вместо карточки —
  // та же ловушка порядка, что у groupDetail/teacherDetail ниже.
  courseDetail: (
    <Route
      path={PATHS.settingsCourseDetail}
      component={CourseDetail}
      key={PATHS.settingsCourseDetail}
    />
  ),
  settings: (
    <Route
      path={PATHS.settings}
      component={PlatformSettings}
      key={PATHS.settings}
    />
  ),
  // ТЗ 0074 (доработка). Отдельный маршрут только для ролей БЕЗ `settings`
  // (FinanceManager): у остальных вкладку рисует сам `settings` — он не
  // `exact`. Стоит ПОСЛЕ `settings`, поэтому админу (все маршруты) ничего не
  // перекрывает и страница не пересоздаётся при переходе между вкладками.
  settingsLmsAccess: (
    <Route
      path={PATHS.settingsLmsAccess}
      component={PlatformSettings}
      key={PATHS.settingsLmsAccess}
    />
  ),
  journal: (
    <Route
      path={`${PATHS.journal}/:journalId`}
      component={Journal}
      key={PATHS.journal}
    />
  ),
  financeDashboard: (
    <Route
      path={PATHS.financesDashboard}
      component={FinanceDashboard}
      key={PATHS.financesDashboard}
    />
  ),
  financePaymentCalendar: (
    <Route
      path={PATHS.financesPaymentCalendar}
      component={PaymentCalendar}
      key={PATHS.financesPaymentCalendar}
    />
  ),
  financeCashFlow: (
    <Route
      path={PATHS.financesCashFlow}
      component={CashFlowReport}
      key={PATHS.financesCashFlow}
    />
  ),
  // ТЗ 0075. `exact` не нужен: глубже `/finances/pnl` путей нет.
  financePnl: (
    <Route
      path={PATHS.financesPnl}
      component={PnlExport}
      key={PATHS.financesPnl}
    />
  ),
  // Detail must precede list — the list route is not `exact` and would
  // otherwise swallow `/finances/invoices/:id`.
  fakturowniaInvoice: (
    <Route
      path={PATHS.financesInvoice}
      component={FakturowniaInvoiceDetail}
      key={PATHS.financesInvoice}
    />
  ),
  fakturowniaInvoices: (
    <Route
      path={PATHS.financesInvoices}
      component={FakturowniaInvoices}
      key={PATHS.financesInvoices}
    />
  ),
  fakturowniaExpenses: (
    <Route
      path={PATHS.financesExpenses}
      component={FakturowniaExpenses}
      key={PATHS.financesExpenses}
    />
  ),
  // Must precede `/finances/invoices` and the planning page —
  // `/finances` matches first because it's the bare prefix.
  finances: (
    <Route
      path={PATHS.finances}
      exact
      component={Finances}
      key={PATHS.finances}
    />
  ),
  teachers: (
    <Route
      path={PATHS.teachers}
      component={TeachersOption}
      key={PATHS.teachers}
    />
  ),
  performance: (
    <Route
      path={PATHS.performance}
      component={Performance}
      key={PATHS.performance}
    />
  ),
  zoom: <Route path={PATHS.zoom} component={ZoomRecordings} key={PATHS.zoom} />,
  // Spec 0010: read-only lesson whiteboard viewer. Same access list as the
  // backend `lessonWhiteboardForErp` gate — ADMIN / ACCOUNT_MANAGER /
  // METHODIST (mirrors the zoom section).
  whiteboardLesson: (
    <Route
      path={PATHS.whiteboardLesson}
      component={LessonWhiteboard}
      key={PATHS.whiteboardLesson}
    />
  ),
  // Spec 0011 survey constructor. Order matters inside the Switch:
  // results (`/surveys/assignments/:id/results`) must precede the `:id`
  // edit route, `/surveys/new` must precede `:id`, and the list route is
  // `exact` so it never swallows the deeper paths.
  surveyResults: (
    <Route
      path={PATHS.surveyResults}
      component={SurveyResults}
      key={PATHS.surveyResults}
    />
  ),
  surveyNew: (
    <Route
      path={PATHS.surveyNew}
      component={SurveyBuilder}
      key={PATHS.surveyNew}
    />
  ),
  // Spec 0027: `/surveys/teachers` and `/surveys/:id/summary` must both
  // precede `surveyEdit` — otherwise `:id` swallows «teachers» as an id.
  surveyTeachers: (
    <Route
      path={PATHS.surveyTeachers}
      exact
      component={SurveyTeachers}
      key={PATHS.surveyTeachers}
    />
  ),
  // Spec 0038: `/surveys/analytics` is another literal segment that MUST
  // precede `surveyEdit`, or `:id` swallows «analytics» as a survey id.
  surveyAnalytics: (
    <Route
      path={PATHS.surveyAnalytics}
      exact
      component={SurveyAnalytics}
      key={PATHS.surveyAnalytics}
    />
  ),
  // Another literal segment that MUST precede `surveyEdit`.
  surveyTags: (
    <Route
      path={PATHS.surveyTags}
      exact
      component={SurveyTags}
      key={PATHS.surveyTags}
    />
  ),
  surveySummary: (
    <Route
      path={PATHS.surveySummary}
      exact
      component={SurveySummary}
      key={PATHS.surveySummary}
    />
  ),
  surveyEdit: (
    <Route
      path={PATHS.surveyEdit}
      exact
      component={SurveyBuilder}
      key={PATHS.surveyEdit}
    />
  ),
  surveys: (
    <Route path={PATHS.surveys} exact component={Surveys} key={PATHS.surveys} />
  ),
  // Spec 0018: tabs are query params (`?tab=`), so a single exact route.
  careerProfessions: (
    <Route
      exact
      path={PATHS.careerProfessions}
      component={CareerProfessions}
      key={PATHS.careerProfessions}
    />
  ),
  careerMedals: (
    <Route
      exact
      path={PATHS.careerMedals}
      component={CareerMedals}
      key={PATHS.careerMedals}
    />
  ),
  careerSiteCourses: (
    <Route
      exact
      path={PATHS.careerSiteCourses}
      component={CareerSiteCourses}
      key={PATHS.careerSiteCourses}
    />
  ),
  careerSkills: (
    <Route
      exact
      path={PATHS.careerSkills}
      component={SkillsDictionary}
      key={PATHS.careerSkills}
    />
  ),
  // 🔴 Уроки и анкеты ВЕРНУЛИСЬ вкладками карьерного центра, но их прежние
  // адреса остаются рабочими редиректами. На `/dictionaries/lessons` ведут
  // закладки операторов и ссылки из писем; молчаливый 404 читался бы как
  // «раздел удалили», а не «раздел переехал».
  careerLessons: (
    <Route
      exact
      path={PATHS.careerLessons}
      key={PATHS.careerLessons}
      render={() => <Redirect to={`${PATHS.careerCenter}?tab=lessons`} />}
    />
  ),
  careerExercises: (
    <Route
      exact
      path={PATHS.careerExercises}
      key={PATHS.careerExercises}
      render={() => <Redirect to={`${PATHS.careerCenter}?tab=exercises`} />}
    />
  ),
  careerCenter: (
    <Route
      path={PATHS.careerCenter}
      exact
      component={CareerCenter}
      key={PATHS.careerCenter}
    />
  ),
  // ТЗ 0024: вкладки живут в `?tab=`, поэтому один exact-роут.
  dofinansowanie: (
    <Route
      path={PATHS.dofinansowanie}
      exact
      component={Dofinansowanie}
      key={PATHS.dofinansowanie}
    />
  ),
  // ТЗ 0076: ящики живут в `?contract=` / `?contractNew=`, один exact-роут.
  contracts: (
    <Route
      path={PATHS.contracts}
      exact
      component={Contracts}
      key={PATHS.contracts}
    />
  ),
  // ТЗ 0078: справочник шаблонов — роли, читающие договоры студентов.
  contractTemplates: (
    <Route
      path={PATHS.contractTemplates}
      exact
      component={ContractTemplates}
      key={PATHS.contractTemplates}
    />
  ),
  // ТЗ 0080: карточка ссылки живёт в `?link=`, один exact-роут. Роли — из
  // `SHORT_LINK_ROLES` (см. `ROLE_LISTED_ROUTES` ниже), не из switch.
  shortLinks: (
    <Route
      path={PATHS.shortLinks}
      exact
      component={ShortLinks}
      key={PATHS.shortLinks}
    />
  ),
  // ТЗ 0081: вкладка и фильтры журнала — в адресе, один exact-роут. Роли —
  // из `SMS_ROLES` (см. `ROLE_LISTED_ROUTES` ниже), не из switch.
  sms: <Route path={PATHS.sms} exact component={Sms} key={PATHS.sms} />,
  // ТЗ 0084: карточка, фильтры и вкладка «Kommo» — в адресе, один
  // exact-роут. Роли — из `LEAD_ROLES` (см. `ROLE_LISTED_ROUTES` ниже).
  leads: <Route path={PATHS.leads} exact component={Leads} key={PATHS.leads} />,
  // ТЗ 0083: «Мой профиль» — всем ролям ERP (`PROFILE_ROLES`).
  profile: (
    <Route path={PATHS.profile} exact component={Profile} key={PATHS.profile} />
  ),
  // ТЗ 0088: вкладки, период, фильтры и спуск — в адресе, один exact-роут.
  // Роли — из `MARKETING_ROLES` (см. `ROLE_LISTED_ROUTES` ниже).
  marketing: (
    <Route
      path={PATHS.marketing}
      exact
      component={Marketing}
      key={PATHS.marketing}
    />
  ),
  dashboard: (
    <Route
      path={PATHS.dashboard}
      component={AccountManagerDashboard}
      key={PATHS.dashboard}
    />
  ),
  communicator: (
    <Route
      path={PATHS.communicator}
      component={Communicator}
      key={PATHS.communicator}
    />
  ),
};

/**
 * Маршруты, чьи роли заданы ОБЩЕЙ константой, а не перечнем в switch ниже:
 * пункт меню и команда палитры берут тот же список, и три места не могут
 * разойтись. Admin получает их и так — через `Object.values(ROUTES)`.
 */
const ROLE_LISTED_ROUTES: Array<{
  route: React.ReactElement;
  roles: UserRole[];
}> = [
  // ТЗ 0080: «Короткие ссылки» — зеркало `SHORTLINK_ROLES` бэкенда.
  { route: ROUTES.shortLinks, roles: SHORT_LINK_ROLES },
  // ТЗ 0081: «SMS» — зеркало `MANAGEMENT_ROLES` бэкенда.
  { route: ROUTES.sms, roles: SMS_ROLES },
  // ТЗ 0084: «Лиды» — зеркало `LEAD_ROLES` бэкенда (Admin, SalesManager,
  // AccountManager); FinanceManager, HR и методист маршрута не получают.
  { route: ROUTES.leads, roles: LEAD_ROLES },
  // ТЗ 0083: «Мой профиль» — зеркало `ERP_LOGIN_ROLES` бэкенда.
  { route: ROUTES.profile, roles: PROFILE_ROLES },
  // ТЗ 0088: «Маркетинг» — зеркало `MARKETING_READ_ROLES` бэкенда (Admin,
  // Marketing). У роли Marketing в switch ниже ветки НЕТ намеренно: кроме
  // этого раздела и профиля ей всё — 404.
  { route: ROUTES.marketing, roles: MARKETING_ROLES },
];

/**
 * Куда вести с корня — первый адрес из этого списка, который есть в
 * маршрутах роли (ТЗ 0039: стартовая страница обязана быть ИЗ МАРШРУТОВ САМОЙ
 * РОЛИ). Порядок сохраняет прежнее поведение ролей, у которых оно было
 * осмысленным; «Маркетинг» — последним: Admin по-прежнему попадает в
 * студентов, а роль Marketing, у которой больше ничего нет, — сюда, а не на
 * 404 сразу после входа.
 */
export const LANDING_CANDIDATES: string[] = [
  PATHS.students,
  PATHS.settingsCourses,
  PATHS.surveys,
  PATHS.careerCenter,
  PATHS.journal,
  PATHS.marketing,
];

/** Стартовый адрес по ключам доступных маршрутов (чистая — для теста). */
export const pickLandingPath = (routeKeys: ReadonlySet<string>): string =>
  LANDING_CANDIDATES.find((path) => routeKeys.has(path)) ??
  PATHS.settingsCourses;

export const AppRouter = () => {
  const getRoutesByRole = useCallback((role: UserRole | undefined) => {
    switch (role) {
      case UserRole.ADMIN:
        return [...Object.values(ROUTES)];
      case UserRole.ACCOUNTANT:
        return [
          ROUTES.dashboard,
          ROUTES.communicator,
          ROUTES.studentDetail,
          ROUTES.students,
          ROUTES.pandadocProvisioning,
          ROUTES.pandadoc,
          ROUTES.debtors,
          ROUTES.mcpAccess,
          ROUTES.debtorsContributions,
          // Must precede `settings` — `/settings` is non-exact and would
          // shadow /settings/groups/:id/details, i.e. the group card would
          // render the settings list instead (same ordering trap as
          // teacherDetail/teachers below).
          ROUTES.groupDetail,
          ROUTES.courseDetail,
          ROUTES.settings,
          ROUTES.journal,
          ROUTES.teacherDetail,
          ROUTES.teachers,
          ROUTES.performance,
          ROUTES.zoom,
          ROUTES.whiteboardLesson,
          ROUTES.surveyResults,
          ROUTES.surveyNew,
          ROUTES.surveyTeachers,
          ROUTES.surveyAnalytics,
          ROUTES.surveyTags,
          ROUTES.surveySummary,
          ROUTES.surveyEdit,
          ROUTES.surveys,
          ROUTES.careerCenter,
          ROUTES.careerExercises,
          ROUTES.careerSkills,
          ROUTES.careerLessons,
          ROUTES.careerProfessions,
          ROUTES.careerMedals,
          ROUTES.careerSiteCourses,
          ROUTES.dofinansowanie,
          // ТЗ 0076: договоры на подпись (студенты и преподаватели).
          ROUTES.contracts,
          ROUTES.contractTemplates,
          ROUTES.financeDashboard,
          ROUTES.fakturowniaInvoice,
          ROUTES.fakturowniaInvoices,
          ROUTES.fakturowniaExpenses,
        ];
      case UserRole.METHODIST:
        // Spec 0011: Methodist is a survey manager role — without these
        // routes the section would be invisible for a pure-Methodist user.
        //
        // ТЗ 0026: + карточка группы и журнал, ТОЛЬКО НА ЧТЕНИЕ. У роли давно
        // были бэкенд-права на ссылку занятия и «Кто заходил», но дотянуться
        // до них было нельзя — эти маршруты ей не выдавались. Чекбоксы
        // посещаемости для неё выключены (updateAttendance методиста не
        // пускает), иначе клик упирался бы в PermissionDenied.
        return [
          ROUTES.groupDetail,
          // ТЗ 0040: методист — тот, кто и заполняет программу курса и
          // привязывает навыки к занятиям, а маршрута в раздел настроек у
          // роли не было вовсе. Карточка обязана идти перед `settings`.
          ROUTES.courseDetail,
          ROUTES.settings,
          ROUTES.journal,
          ROUTES.zoom,
          ROUTES.whiteboardLesson,
          ROUTES.surveyResults,
          ROUTES.surveyNew,
          ROUTES.surveyTeachers,
          ROUTES.surveyAnalytics,
          ROUTES.surveyTags,
          ROUTES.surveySummary,
          ROUTES.surveyEdit,
          ROUTES.surveys,
          // Spec 0038 revision: Methodist owns surveys and the backend already
          // lets them read a student's answers and a teacher's feedback — but
          // without these routes every «разобраться в сигнале» link 404'd.
          // The cards gate their own money/hiring tabs by role, and the
          // backend refuses those queries regardless.
          ROUTES.studentDetail,
          ROUTES.teacherDetail,
          ROUTES.careerCenter,
          ROUTES.careerExercises,
          ROUTES.careerSkills,
          ROUTES.careerLessons,
          ROUTES.careerProfessions,
          ROUTES.careerMedals,
          ROUTES.careerSiteCourses,
        ];
      case UserRole.SALESMANAGER:
        return [
          ROUTES.dashboard,
          ROUTES.communicator,
          ROUTES.studentDetail,
          ROUTES.students,
          ROUTES.pandadocProvisioning,
          ROUTES.pandadoc,
          ROUTES.debtors,
          ROUTES.mcpAccess,
          ROUTES.debtorsContributions,
          // Must precede `settings` — see the note in ACCOUNTANT above.
          ROUTES.groupDetail,
          ROUTES.settings,
          ROUTES.journal,
          ROUTES.teacherDetail,
          ROUTES.teachers,
          // Read access only; the linking mutations gate by FinanceManager.
          ROUTES.financeDashboard,
          ROUTES.fakturowniaInvoice,
          ROUTES.fakturowniaInvoices,
          // ТЗ 0024: SalesManager ведёт воронку дофинансирования.
          ROUTES.dofinansowanie,
          // ТЗ 0076: SalesManager оформляет договоры на подпись.
          ROUTES.contracts,
          ROUTES.contractTemplates,
        ];
      case UserRole.HRMANAGER:
        return [
          // Must precede `settings` — see the note in ACCOUNTANT above.
          ROUTES.groupDetail,
          ROUTES.courseDetail,
          ROUTES.settings,
          ROUTES.journal,
          // Must precede `teachers` — the list route is non-exact and would
          // shadow /teachers/:id/details (same ordering as ACCOUNTANT above).
          ROUTES.teacherDetail,
          ROUTES.teachers,
          ROUTES.performance,
          // Spec 0014: HR files tasks about teachers, so they need the board.
          ROUTES.dashboard,
          // Spec 0018: HR runs the career centre alongside Admin/AM/Methodist.
          ROUTES.careerCenter,
          ROUTES.careerExercises,
          ROUTES.careerSkills,
          ROUTES.careerLessons,
          ROUTES.careerProfessions,
          ROUTES.careerMedals,
          ROUTES.careerSiteCourses,
          // Spec 0027: HR reads the teacher survey leaderboard and the
          // per-teacher feedback tab, but not the survey constructor.
          ROUTES.surveyTeachers,
          // Spec 0038: …and the aggregate analytics table. The named detail
          // (comments, a student's answers) is denied by the backend, so the
          // route alone grants nothing extra — a route is not a permission.
          ROUTES.surveyAnalytics,
          // ТЗ 0023: HR входит в MANAGEMENT_ROLES, то есть подключить ИИ ему
          // можно и бэкенд его пускает. Без этого роута он не мог ни узнать
          // адрес эндпойнта, ни отозвать своё подключение: страница отдавала
          // 404, хотя доступ был выдан и работал.
          ROUTES.mcpAccess,
          // ТЗ 0076: HR оформляет документы преподавателей на подпись —
          // бэкенд отдаёт ему только их, студенческие договоры не видны.
          ROUTES.contracts,
        ];
      case UserRole.FINANCIER:
        return [
          // ТЗ 0074 (доработка): из раздела настроек финансисту — только
          // вкладка приостановки доступа (правит её вместе с админом).
          ROUTES.settingsLmsAccess,
          ROUTES.dashboard,
          ROUTES.communicator,
          ROUTES.pandadocProvisioning,
          ROUTES.pandadoc,
          ROUTES.debtors,
          ROUTES.mcpAccess,
          ROUTES.debtorsContributions,
          ROUTES.studentDetail,
          ROUTES.students,
          ROUTES.groupDetail,
          ROUTES.teacherDetail,
          ROUTES.finances,
          ROUTES.financeDashboard,
          // ТЗ 0024: только чтение — на запись бэкенд не пускает.
          ROUTES.dofinansowanie,
          // ТЗ 0076: договоры на подпись — только чтение.
          ROUTES.contracts,
          ROUTES.contractTemplates,
          ROUTES.financePaymentCalendar,
          ROUTES.financeCashFlow,
          // ТЗ 0075: выгрузка для P&L — те же роли, что у плана по году.
          ROUTES.financePnl,
          ROUTES.fakturowniaInvoice,
          ROUTES.fakturowniaInvoices,
          ROUTES.fakturowniaExpenses,
        ];
      default:
        return [];
    }
  }, []);
  const userRoles = useReactiveVar(userStateVar).me?.roles;
  const availableRoutes = userRoles
    ? userRoles.map((role) => getRoutesByRole(role as UserRole))
    : [];
  const switchedRoutes = availableRoutes.reduce(
    (prev, curr) => [...prev, ...curr],
    [],
  );
  const roleListedRoutes = ROLE_LISTED_ROUTES.filter(
    ({ route, roles }) =>
      !switchedRoutes.includes(route) &&
      (userRoles ?? []).some((role) => roles.includes(role as UserRole)),
  ).map(({ route }) => route);
  const computedRoutes = switchedRoutes.concat(roleListedRoutes);

  /**
   * Куда вести с корня. Стартовая страница обязана быть ИЗ МАРШРУТОВ САМОЙ
   * РОЛИ (ТЗ 0039).
   *
   * Раньше выбор был из двух: `/students`, если роль его имеет, иначе всегда
   * `/settings/courses`. Роль, у которой нет ни того, ни другого, попадала
   * сразу после входа на 404 — и это не гипотеза: ровно так встретил ERP
   * методиста, как только ему починили сам вход. Симптом читается как «мне
   * не дали доступ», хотя разделы у роли есть, до них просто нельзя дойти
   * без ручного набора адреса.
   *
   * Порядок кандидатов сохраняет прежнее поведение для всех ролей, у которых
   * оно было осмысленным: `/students` и `/settings/courses` идут первыми.
   */
  const landingPath = useMemo(
    () =>
      pickLandingPath(new Set(computedRoutes.map((rout) => String(rout.key)))),
    [computedRoutes],
  );

  return (
    <Suspense fallback={<Splash />}>
      <Switch>
        <Redirect exact from={PATHS.main} to={landingPath} />
        <Redirect exact from={PATHS.settings} to={PATHS.settingsCourses} />
        <Redirect exact from={PATHS.teachers} to={PATHS.teachersUsers} />
        <Redirect
          exact
          from={PATHS.debtors}
          to={`${PATHS.contributions}/all`}
        />
        {computedRoutes}
        <Route path="*" render={() => <ErrorPage status={404} />} />
      </Switch>
    </Suspense>
  );
};
